import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { runRedTeamAnalysis } from './dummyAi'

const BRIEF = {
  code: 'DS/2026/HĐMB-ABC-012',
  type: 'Dân sự – Tranh chấp HĐMB căn hộ',
  role: 'Nguyên đơn',
  amount: '3.200.000.000 VNĐ',
}

const DEFAULT_ARG =
  'Bên bị đơn vi phạm nghĩa vụ bàn giao căn hộ đúng thời hạn và có dấu hiệu không thống nhất mốc hiệu lực hợp đồng giữa Điều 7 và Điều 12. Đề nghị Tòa án xác định mốc hiệu lực được các bên thừa nhận qua email/biên bản, buộc bị đơn thực hiện nghĩa vụ bàn giao hoặc bồi thường thiệt hại; xem xét phạt vi phạm theo mức hợp lý, có đối chiếu chứng từ thanh toán và timeline giao dịch.'

export function StrategyAdvisorTab() {
  const theme = useTheme()
  const [argument, setArgument] = useState(DEFAULT_ARG)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<Awaited<ReturnType<typeof runRedTeamAnalysis>> | null>(null)
  const [forecastOpen, setForecastOpen] = useState(false)

  const defaultResult = useMemo<Awaited<ReturnType<typeof runRedTeamAnalysis>>>(
    () => ({
      weaknesses: [
        {
          severity: 'high',
          point: 'Chưa khóa “mốc hiệu lực” bằng 1 nguồn chứng cứ ưu tiên',
          detail:
            'Luận cứ đang nói đến mâu thuẫn Điều 7/Điều 12, nhưng chưa xác định rõ “mốc hiệu lực nào” được các bên xác nhận (email/biên bản/sao kê). Bị đơn có thể phản biện rằng mốc bạn chọn chỉ là dự kiến hoặc chưa đủ điều kiện có hiệu lực.',
          suggestion:
            'Chọn 1 mốc hiệu lực làm “mốc chính” và chứng minh theo thứ tự ưu tiên: (1) thỏa thuận/trao đổi email xác nhận, (2) biên bản/phiếu thu – chứng từ thanh toán kèm điều kiện hiệu lực, (3) hành vi thực hiện hợp đồng; lập bảng timeline kèm trích dẫn nguồn.',
        },
        {
          severity: 'medium',
          point: 'Thiếu cấu trúc yêu cầu (claims) + phương án dự phòng theo từng kịch bản',
          detail:
            'Hiện mới nêu “buộc bàn giao hoặc bồi thường”, nhưng chưa chia rõ các yêu cầu độc lập: (i) xác định vi phạm nghĩa vụ, (ii) buộc thực hiện đúng, (iii) bồi thường thiệt hại, (iv) lãi chậm trả, (v) phạt vi phạm (nếu có). Nếu Tòa không chấp nhận phạt, cần phương án chuyển sang bồi thường thiệt hại có chứng cứ.',
          suggestion:
            'Viết theo cấu trúc 3 tầng: Yêu cầu chính → Yêu cầu thay thế → Yêu cầu bổ sung. Mỗi yêu cầu gắn 1 nhóm chứng cứ và 1 giả định pháp lý (hiệu lực – vi phạm – thiệt hại – quan hệ nhân quả).',
        },
      ],
      riskForecast: {
        winFull: 32,
        winPartial: 58,
        lose: 10,
        basedOn: 912,
        keyFactors: [
          { factor: 'Có biên bản bàn giao/biên bản làm việc thể hiện tiến độ', impact: 14 },
          { factor: 'Có sao kê/phiếu thu chứng minh thanh toán đúng mốc', impact: 12 },
          { factor: 'Mốc hiệu lực hợp đồng được email xác nhận rõ', impact: 10 },
          { factor: 'Điều khoản phạt vi phạm quá cao/khó bảo vệ', impact: -8 },
        ],
      },
      strategySuggestions: [
        'Chuẩn hóa 01 mốc hiệu lực: trích email/biên bản xác nhận + lập timeline theo ngày/tháng.',
        'Lập bảng thanh toán (sao kê/phiếu thu/ủy nhiệm chi) đối chiếu tiến độ – nghĩa vụ.',
        'Chuẩn bị 2 gói yêu cầu: buộc bàn giao (nếu còn khả thi) hoặc bồi thường thiệt hại (nếu bàn giao không thể).',
        'Giảm “phạt vi phạm” về mức hợp lý hoặc chuyển trọng tâm sang thiệt hại thực tế có chứng cứ.',
      ],
    }),
    [],
  )

  useEffect(() => {
    // Yêu cầu: vào tab là hiển thị kết quả Red Team luôn (mockup).
    setResult(defaultResult)
    setRunning(false)
    setForecastOpen(true)
  }, [defaultResult])

  const runRedTeam = async () => {
    setForecastOpen(false)
    setRunning(true)
    setResult(null)
    try {
      const r = await runRedTeamAnalysis(BRIEF.code, argument)
      setResult(r)
    } finally {
      setRunning(false)
    }
  }

  const runForecast = () => setForecastOpen((v) => !v)

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          borderRadius: 2,
          mb: 2,
          border: 1,
          borderColor: 'divider',
          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} useFlexGap flexWrap="wrap">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.15 }}>
              Cố vấn chiến lược & Red Team
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              Use case: tranh chấp HĐMB căn hộ — rà soát điểm yếu luận cứ và dự báo kết quả
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Vụ án: ${BRIEF.code}`} variant="outlined" />
            <Chip size="small" label={BRIEF.type} variant="outlined" />
            <Chip size="small" label={`Vai trò: ${BRIEF.role}`} sx={{ bgcolor: alpha(theme.palette.info.main, 0.10) }} />
            <Chip size="small" label={`Tranh chấp: ${BRIEF.amount}`} sx={{ bgcolor: alpha(theme.palette.warning.main, 0.14) }} />
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 2,
              boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary' }}>
              LUẬN CỨ CỦA TÔI
            </Typography>
            <TextField
              multiline
              minRows={5}
              fullWidth
              value={argument}
              onChange={(e) => setArgument(e.target.value)}
              placeholder="Nhập luận cứ/lập luận của bạn tại đây..."
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" color="error" onClick={runRedTeam} disabled={running}>
                Chạy Red Team AI
              </Button>
              <Button variant={forecastOpen ? 'contained' : 'outlined'} onClick={runForecast} sx={{ ml: 'auto' }}>
                Dự báo kết quả
              </Button>
            </Stack>
          </Paper>

          {forecastOpen && (
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                p: 2,
                boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.primary.dark }}>
                  DỰ BÁO KẾT QUẢ
                </Typography>
                <Box
                  component="span"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.dark,
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {defaultResult.riskForecast.basedOn} vụ tương tự
                </Box>
              </Stack>
              <Stack spacing={1}>
                <ForecastBar label="Thắng hoàn toàn" pct={result?.riskForecast.winFull ?? defaultResult.riskForecast.winFull} color={theme.palette.success.main} />
                <ForecastBar label="Thắng một phần" pct={result?.riskForecast.winPartial ?? defaultResult.riskForecast.winPartial} color={theme.palette.info.main} />
                <ForecastBar label="Thua kiện" pct={result?.riskForecast.lose ?? defaultResult.riskForecast.lose} color={theme.palette.error.main} />
              </Stack>
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={700}>
                Yếu tố tác động chính:
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {(result?.riskForecast.keyFactors ?? defaultResult.riskForecast.keyFactors).slice(0, 4).map((k) => (
                  <Typography key={k.factor} variant="body2" sx={{ color: k.impact >= 0 ? theme.palette.success.dark : theme.palette.error.dark }}>
                    {k.impact >= 0 ? '+' : '-'} {k.factor} → {k.impact >= 0 ? '+' : ''}
                    {k.impact}%
                  </Typography>
                ))}
              </Stack>
            </Paper>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!result && !running && (
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 5,
                textAlign: 'center',
                color: 'text.secondary',
                boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
              }}
            >
              <Typography variant="h5" sx={{ mb: 1 }}>
                AI
              </Typography>
              <Typography variant="body2">Nhấn &quot;Chạy Red Team AI&quot; để AI phản biện luận cứ của bạn</Typography>
            </Paper>
          )}

          {(running || result) && (
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderLeft: `3px solid ${theme.palette.error.main}`,
                borderRadius: 2,
                p: 2,
                boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
                bgcolor: 'background.paper',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.palette.error.dark }}>
                  Red Team AI – Phản biện
                </Typography>
              </Stack>

              {running && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <PulseDot delay={0} />
                  <PulseDot delay={0.2} />
                  <PulseDot delay={0.4} />
                  <Typography variant="body2">AI đang phân tích luận cứ...</Typography>
                </Stack>
              )}

              {result && (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Badge label="ĐIỂM YẾU 1" tone="danger" />
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                      {result.weaknesses[0]?.point}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {result.weaknesses[0]?.detail}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Badge label="ĐIỂM YẾU 2" tone="warn" />
                    <Typography variant="body2" fontWeight={700} sx={{ mt: 0.75 }}>
                      {result.weaknesses[1]?.point}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {result.weaknesses[1]?.detail}
                    </Typography>
                  </Box>
                  <Box sx={{ borderTop: 1, borderColor: 'divider', my: 1.5 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={700} sx={{ mb: 1 }}>
                    Gợi ý tăng cường hồ sơ:
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', mb: 0.5 }}>
                    1. Bổ sung sao kê ngân hàng chung 2018-2024
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main', mb: 0.5 }}>
                    2. Yêu cầu định giá độc lập BĐS
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'primary.main' }}>
                    3. Chuẩn bị nhân chứng xác nhận nguồn vốn
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  )
}

function ForecastBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={700}>
          {pct}%
        </Typography>
      </Stack>
      <Box sx={{ height: 4, bgcolor: alpha('#000', 0.08), borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, transition: 'width 0.4s' }} />
      </Box>
    </Box>
  )
}

function Badge({ label, tone }: { label: string; tone: 'danger' | 'warn' }) {
  const theme = useTheme()
  const bg = tone === 'danger' ? alpha(theme.palette.error.main, 0.12) : alpha(theme.palette.warning.main, 0.18)
  const fg = tone === 'danger' ? theme.palette.error.dark : theme.palette.warning.dark
  return (
    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, bgcolor: bg, color: fg, fontSize: 10, fontWeight: 700 }}>
      {label}
    </Box>
  )
}

function PulseDot({ delay }: { delay: number }) {
  const theme = useTheme()
  return (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: theme.palette.primary.main,
        animation: 'pulse 0.8s ease-in-out infinite alternate',
        animationDelay: `${delay}s`,
        '@keyframes pulse': { from: { opacity: 0.3, transform: 'scale(0.8)' }, to: { opacity: 1, transform: 'scale(1)' } },
      }}
    />
  )
}
